import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
    Box,
    Divider,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

let mermaidLoaderPromise = null;

const escapeHtml = (value) =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const slugify = (value) =>
    String(value || '')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80) || 'section';

const renderInlineMarkdown = (value) => {
    const codeTokens = [];
    const linkTokens = [];

    let text = String(value || '');
    text = text.replace(/`([^`]+)`/g, (_match, code) => {
        const token = `__CODE_${codeTokens.length}__`;
        codeTokens.push(`<code>${escapeHtml(code)}</code>`);
        return token;
    });
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label, url) => {
        const safeUrl = escapeHtml(url);
        const safeLabel = escapeHtml(label);
        const token = `__LINK_${linkTokens.length}__`;
        // Same-page anchors (#section) navigate in-place; everything else opens
        // a new tab. Lets glossary-style links like [ATM](#atm) jump to the
        // glossary heading without opening a new browser tab.
        const isAnchor = url.startsWith('#');
        const targetAttr = isAnchor ? '' : ' target="_blank" rel="noreferrer"';
        linkTokens.push(
            `<a href="${safeUrl}"${targetAttr}>${safeLabel}</a>`
        );
        return token;
    });

    text = escapeHtml(text);
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    text = text.replace(/__CODE_(\d+)__/g, (_match, index) => codeTokens[Number(index)] || '');
    text = text.replace(/__LINK_(\d+)__/g, (_match, index) => linkTokens[Number(index)] || '');
    return text;
};

const isHorizontalRule = (line) => /^---+$/.test(line.trim());
const isCodeFence = (line) => line.trim().startsWith('```');
const isHeading = (line) => /^(#{1,6})\s+/.test(line);
const isBlockquote = (line) => /^>\s?/.test(line.trim());
const isOrderedItem = (line) => /^\d+\.\s+/.test(line.trim());
const isUnorderedItem = (line) => /^-\s+/.test(line.trim());
const isTableDivider = (line) => /^\|\s*[:\-| ]+\|$/.test(line.trim());
const isTableRow = (line) => /^\|.*\|$/.test(line.trim());
const isTableStart = (line, nextLine) => isTableRow(line) && isTableDivider(nextLine || '');

const splitTableRow = (line) =>
    line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());

const isSpecialStart = (line, nextLine) =>
    !line.trim()
    || isCodeFence(line)
    || isHeading(line)
    || isHorizontalRule(line)
    || isBlockquote(line)
    || isOrderedItem(line)
    || isUnorderedItem(line)
    || isTableStart(line, nextLine);

function parseMarkdown(markdown) {
    const lines = String(markdown || '').replace(/\r\n/g, '\n').split('\n');
    const blocks = [];
    const headingCounts = new Map();
    let index = 0;

    while (index < lines.length) {
        const line = lines[index];

        if (!line.trim()) {
            index += 1;
            continue;
        }

        if (isCodeFence(line)) {
            const language = line.trim().slice(3).trim().toLowerCase();
            const content = [];
            index += 1;
            while (index < lines.length && !isCodeFence(lines[index])) {
                content.push(lines[index]);
                index += 1;
            }
            if (index < lines.length && isCodeFence(lines[index])) {
                index += 1;
            }
            blocks.push({
                type: 'code',
                language,
                content: content.join('\n'),
            });
            continue;
        }

        if (isHorizontalRule(line)) {
            blocks.push({ type: 'hr' });
            index += 1;
            continue;
        }

        const headingMatch = /^(#{1,6})\s+(.*)$/.exec(line);
        if (headingMatch) {
            const level = headingMatch[1].length;
            const text = headingMatch[2].trim();
            const baseId = slugify(text);
            const nextCount = (headingCounts.get(baseId) || 0) + 1;
            headingCounts.set(baseId, nextCount);
            blocks.push({
                type: 'heading',
                level,
                text,
                id: nextCount === 1 ? baseId : `${baseId}-${nextCount}`,
            });
            index += 1;
            continue;
        }

        if (isTableStart(line, lines[index + 1])) {
            const header = splitTableRow(line);
            const rows = [];
            index += 2;
            while (index < lines.length && isTableRow(lines[index])) {
                rows.push(splitTableRow(lines[index]));
                index += 1;
            }
            blocks.push({
                type: 'table',
                header,
                rows,
            });
            continue;
        }

        if (isBlockquote(line)) {
            const content = [];
            while (index < lines.length && isBlockquote(lines[index])) {
                content.push(lines[index].trim().replace(/^>\s?/, ''));
                index += 1;
            }
            blocks.push({
                type: 'blockquote',
                content: content.join(' '),
            });
            continue;
        }

        if (isOrderedItem(line)) {
            const items = [];
            while (index < lines.length && isOrderedItem(lines[index])) {
                items.push(lines[index].trim().replace(/^\d+\.\s+/, ''));
                index += 1;
            }
            blocks.push({ type: 'ordered-list', items });
            continue;
        }

        if (isUnorderedItem(line)) {
            const items = [];
            while (index < lines.length && isUnorderedItem(lines[index])) {
                items.push(lines[index].trim().replace(/^-\s+/, ''));
                index += 1;
            }
            blocks.push({ type: 'unordered-list', items });
            continue;
        }

        const paragraph = [line.trim()];
        index += 1;
        while (
            index < lines.length
            && !isSpecialStart(lines[index], lines[index + 1])
        ) {
            paragraph.push(lines[index].trim());
            index += 1;
        }
        blocks.push({
            type: 'paragraph',
            content: paragraph.join(' '),
        });
    }

    return blocks;
}

function loadMermaid() {
    if (typeof window === 'undefined') {
        return Promise.resolve(null);
    }

    if (window.mermaid) {
        return Promise.resolve(window.mermaid);
    }

    if (mermaidLoaderPromise) {
        return mermaidLoaderPromise;
    }

    mermaidLoaderPromise = new Promise((resolve) => {
        const existing = document.querySelector('script[data-insights-mermaid="true"]');
        if (existing) {
            existing.addEventListener('load', () => resolve(window.mermaid || null), { once: true });
            existing.addEventListener('error', () => resolve(null), { once: true });
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js';
        script.async = true;
        script.dataset.insightsMermaid = 'true';
        script.onload = () => resolve(window.mermaid || null);
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
    });

    return mermaidLoaderPromise;
}

function MermaidDiagram({ code }) {
    const theme = useTheme();
    const containerRef = useRef(null);
    const diagramId = useId().replace(/:/g, '-');
    const [renderFailed, setRenderFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;

        setRenderFailed(false);

        loadMermaid()
            .then(async (mermaid) => {
                if (cancelled || !containerRef.current || !mermaid) {
                    if (!cancelled) {
                        setRenderFailed(true);
                    }
                    return;
                }

                try {
                    mermaid.initialize({
                        startOnLoad: false,
                        securityLevel: 'loose',
                        theme: theme.palette.mode === 'dark' ? 'dark' : 'default',
                    });

                    const rendered = await mermaid.render(`${diagramId}-${theme.palette.mode}`, code);
                    if (cancelled || !containerRef.current) {
                        return;
                    }

                    containerRef.current.innerHTML = rendered.svg;
                    rendered.bindFunctions?.(containerRef.current);
                } catch (_error) {
                    if (!cancelled) {
                        setRenderFailed(true);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setRenderFailed(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [code, diagramId, theme.palette.mode]);

    if (renderFailed) {
        return (
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                    Diagram source
                </Typography>
                <Box
                    component="pre"
                    sx={{
                        m: 0,
                        p: 2,
                        overflowX: 'auto',
                        borderRadius: 2,
                        bgcolor: (currentTheme) =>
                            currentTheme.palette.mode === 'dark'
                                ? alpha(currentTheme.palette.common.black, 0.24)
                                : alpha(currentTheme.palette.common.black, 0.04),
                        color: (currentTheme) =>
                            currentTheme.palette.mode === 'dark'
                                ? alpha(currentTheme.palette.common.white, 0.87)
                                : alpha(currentTheme.palette.common.black, 0.87),
                        border: '1px solid',
                        borderColor: 'divider',
                        fontSize: '0.84rem',
                        lineHeight: 1.6,
                    }}
                >
                    {code}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: (currentTheme) =>
                    currentTheme.palette.mode === 'dark'
                        ? alpha(currentTheme.palette.common.black, 0.18)
                        : alpha(currentTheme.palette.common.white, 0.5),
                px: { xs: 1.5, md: 2.5 },
                py: 2,
                overflowX: 'auto',
                '& svg': {
                    display: 'block',
                    maxWidth: '100%',
                    height: 'auto',
                    margin: '0 auto',
                },
            }}
            ref={containerRef}
        />
    );
}

function MarkdownBlock({ block }) {
    if (block.type === 'heading') {
        const variantMap = {
            1: 'h3',
            2: 'h4',
            3: 'h5',
            4: 'h6',
        };

        return (
            <Typography
                id={block.id}
                variant={variantMap[block.level] || 'subtitle1'}
                sx={{
                    mt: block.level <= 2 ? 4 : 3,
                    mb: 1.5,
                    scrollMarginTop: 96,
                    fontWeight: block.level <= 2 ? 700 : 600,
                    letterSpacing: '-0.02em',
                }}
                dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(block.text) }}
            />
        );
    }

    if (block.type === 'paragraph') {
        return (
            <Typography
                component="p"
                sx={{
                    mb: 1.75,
                    color: 'text.primary',
                    lineHeight: 1.78,
                    fontSize: '1rem',
                }}
                dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(block.content) }}
            />
        );
    }

    if (block.type === 'unordered-list' || block.type === 'ordered-list') {
        const Component = block.type === 'ordered-list' ? 'ol' : 'ul';
        return (
            <Box
                component={Component}
                sx={{
                    pl: 3,
                    mb: 2,
                    mt: 0,
                    '& li': {
                        mb: 0.9,
                        lineHeight: 1.7,
                    },
                    '& li:last-of-type': {
                        mb: 0,
                    },
                }}
            >
                {block.items.map((item) => (
                    <li key={`${block.type}-${item}`}>
                        <span dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(item) }} />
                    </li>
                ))}
            </Box>
        );
    }

    if (block.type === 'blockquote') {
        return (
            <Box
                sx={{
                    pl: 2,
                    pr: 1,
                    py: 0.75,
                    mb: 2.5,
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                    bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.12)
                            : alpha(theme.palette.primary.main, 0.08),
                    borderRadius: 1.5,
                }}
            >
                <Typography
                    component="blockquote"
                    sx={{
                        m: 0,
                        fontStyle: 'italic',
                        lineHeight: 1.75,
                    }}
                    dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(block.content) }}
                />
            </Box>
        );
    }

    if (block.type === 'table') {
        return (
            <TableContainer
                sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {block.header.map((cell) => (
                                <TableCell
                                    key={cell}
                                    sx={{
                                        fontWeight: 700,
                                        bgcolor: (theme) =>
                                            theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.common.white, 0.04)
                                                : alpha(theme.palette.common.black, 0.03),
                                    }}
                                    dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(cell) }}
                                />
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {block.rows.map((row, rowIndex) => (
                            <TableRow key={`${rowIndex}-${row.join('|')}`}>
                                {row.map((cell, cellIndex) => (
                                    <TableCell
                                        key={`${rowIndex}-${cellIndex}`}
                                        sx={{ verticalAlign: 'top' }}
                                        dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(cell) }}
                                    />
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    if (block.type === 'code') {
        if (block.language === 'mermaid') {
            return (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                        Architecture diagram
                    </Typography>
                    <MermaidDiagram code={block.content} />
                </Box>
            );
        }

        return (
            <Box
                component="pre"
                sx={{
                    m: 0,
                    mb: 3,
                    p: 2,
                    overflowX: 'auto',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.black, 0.24)
                            : alpha(theme.palette.common.black, 0.04),
                    color: (theme) =>
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.87)
                            : alpha(theme.palette.common.black, 0.87),
                    fontSize: '0.84rem',
                    lineHeight: 1.65,
                }}
            >
                {block.content}
            </Box>
        );
    }

    if (block.type === 'hr') {
        return <Divider sx={{ my: 3 }} />;
    }

    return null;
}

export function MarkdownDocument({ markdown }) {
    const blocks = useMemo(() => parseMarkdown(markdown), [markdown]);

    return (
        <Box
            sx={{
                '& a': {
                    color: 'primary.main',
                },
                '& code': {
                    fontFamily: '"SFMono-Regular", "Roboto Mono", Consolas, monospace',
                    fontSize: '0.9em',
                    px: 0.65,
                    py: 0.2,
                    borderRadius: 1,
                    bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.common.white, 0.08)
                            : alpha(theme.palette.common.black, 0.05),
                    color: (theme) =>
                        theme.palette.mode === 'dark'
                            ? '#e2e8f0'
                            : '#1e293b',
                },
                '& strong': {
                    fontWeight: 700,
                },
            }}
        >
            {blocks.map((block, index) => (
                <MarkdownBlock
                    key={`${block.type}-${block.id || block.language || index}`}
                    block={block}
                />
            ))}
        </Box>
    );
}

export function MarkdownAnchorLink({ href, children }) {
    return (
        <Link
            href={href}
            underline="none"
            sx={{
                color: 'text.secondary',
                fontSize: '0.9rem',
                '&:hover': {
                    color: 'primary.main',
                },
            }}
        >
            {children}
        </Link>
    );
}
